import { ReactNode, useEffect, useMemo, useState } from "react";

import { useNavigate } from "react-router-dom";
import {
  AlertCircle,
  ArrowRight,
  CalendarClock,
  ChevronDown,
  ExternalLink,
  Globe,
  LayoutDashboard,
  Loader2,
  Search,
  X,
} from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

import {
  CRON_5_FIELD_REGEX,
  DEFAULT_MONTHLY,
  MONTH_WEEKS,
  MonthlyMode,
  WEEKDAYS,
  clampMonthDay,
  monthDaySkipsMonths,
  monthlyCron,
  parseMonthlyCron,
} from "./broadcast-schedule";
import { CHANNEL_LABELS, CHANNEL_ORDER, ChannelIcon } from "./channel-icon";
import {
  CreateBroadcastChannel,
  CreateBroadcastDto,
  DeliveryMode,
  Broadcast,
  filterSlackChannels,
  useSlackChannels,
  useSlackConnections,
  isAutoPaused,
} from "./use-broadcasts";

/** A project offered in the workspace-mode multi-project picker. */
export interface WorkspaceProjectOption {
  id: string;
  name: string;
  /** Only published projects can be captured; unpublished ones show disabled. */
  published: boolean;
}

interface BroadcastFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * "project" (default): capture THIS project — the per-project tab.
   * "workspace": pick N published projects + a delivery mode — the workspace page.
   */
  mode?: "project" | "workspace";
  /** Required in "project" mode — the project this broadcast captures. */
  projectId?: string;
  /** Published projects to choose from, "workspace" mode only. */
  projects?: WorkspaceProjectOption[];
  broadcast?: Broadcast | null;
  onSubmit: (dto: CreateBroadcastDto, opts?: { test?: boolean }) => Promise<void>;
}

type SchedulePreset =
  | "hourly"
  | "every-6h"
  | "weekdays-9"
  | "twice-daily"
  | "daily-9"
  | "weekly"
  | "monthly"
  | "custom";

// Fixed-cron presets. "weekly" (day-of-week picker) and "monthly" (three modes) build their own cron.
const SCHEDULE_PRESETS: { value: SchedulePreset; label: string; cron?: string }[] = [
  { value: "hourly", label: "Every hour", cron: "0 * * * *" },
  { value: "every-6h", label: "Every 6 hours", cron: "0 */6 * * *" },
  { value: "weekdays-9", label: "Every weekday · 09:00", cron: "0 9 * * 1-5" },
  { value: "twice-daily", label: "Twice daily · 09:00 & 17:00", cron: "0 9,17 * * *" },
  { value: "daily-9", label: "Every day · 09:00", cron: "0 9 * * *" },
  { value: "weekly", label: "Weekly · 09:00" },
  { value: "monthly", label: "Monthly · 09:00" },
  { value: "custom", label: "Custom (cron)" },
];

const PRESET_CRON: Record<string, string> = Object.fromEntries(
  SCHEDULE_PRESETS.filter((p) => p.cron).map((p) => [p.value, p.cron as string]),
);

/** cron for the weekly preset on a given day-of-week at 09:00. */
function weeklyCron(dow: string): string {
  return `0 9 * * ${dow}`;
}

/** If the cron is a single-day weekly at 09:00, return that day-of-week. */
function weeklyDayOf(cron: string): string | null {
  const m = /^0 9 \* \* ([0-6])$/.exec(cron.trim());
  return m ? m[1] : null;
}

/** Common IANA zones for the picker; the browser tz is injected + defaulted. */
const COMMON_TIMEZONES = [
  "UTC",
  "America/Los_Angeles",
  "America/Denver",
  "America/Chicago",
  "America/New_York",
  "America/Sao_Paulo",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Europe/Madrid",
  "Europe/Moscow",
  "Africa/Cairo",
  "Asia/Dubai",
  "Asia/Kolkata",
  "Asia/Bangkok",
  "Asia/Ho_Chi_Minh",
  "Asia/Saigon",
  "Asia/Singapore",
  "Asia/Shanghai",
  "Asia/Hong_Kong",
  "Asia/Tokyo",
  "Asia/Seoul",
  "Australia/Sydney",
  "Pacific/Auckland",
];

const BROWSER_TZ =
  Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Chicago";

function timezoneOptions(current: string): string[] {
  const set = new Set<string>([BROWSER_TZ, current, ...COMMON_TIMEZONES]);
  return [...set].filter(Boolean);
}

function cronToPreset(cron: string): SchedulePreset {
  const match = SCHEDULE_PRESETS.find((p) => p.cron === cron);
  if (match) return match.value;
  if (weeklyDayOf(cron)) return "weekly";
  if (parseMonthlyCron(cron)?.hour === 9) return "monthly";
  return "custom";
}

/** Parse comma/space-separated channel IDs into a trimmed, de-duped list. */
function parseChannelIds(raw: string): string[] {
  return Array.from(
    new Set(
      raw
        .split(/[\s,]+/)
        .map((s) => s.trim())
        .filter(Boolean),
    ),
  );
}

/** Basic email shape check for the recipients field (backend re-validates). */
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
/**
 * Help article under the Teams webhook URL field — the OptiSigns Broadcast form links the same
 * one (kaze#86): a channel link or a page URL pasted here is refused by the engine (engine#3).
 */
const TEAMS_WEBHOOK_HELP_URL = "https://links.optisigns.com/4hoQyTp";

/** "Oct 13, 2026 · 09:02 UTC" — matches the paused email's date line. */
function formatPausedAt(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return (
    d.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" }) +
    " · " +
    d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "UTC" }) +
    " UTC"
  );
}

/** Split a comma/space/newline separated recipients string into unique addresses. */
function parseRecipients(raw: string): string[] {
  return Array.from(
    new Set(
      raw
        .split(/[\s,;]+/)
        .map((s) => s.trim())
        .filter(Boolean),
    ),
  );
}

type ChannelKey = "slack" | "teams" | "email" | "pdf" | "webhook";

const DELIVERY_MODES: { value: DeliveryMode; title: string; desc: string }[] = [
  { value: "separate", title: "Separate", desc: "one message per project" },
  {
    value: "combined-images",
    title: "Combined · images",
    desc: "every capture in one message",
  },
  { value: "combined-pdf", title: "Combined · PDF", desc: "a page per project" },
];

/** Webhook host for the collapsed-card summary; falls back to the raw value. */
function hostOf(url: string): string {
  try {
    return new URL(url).host || url;
  } catch {
    return url;
  }
}

/**
 * One added delivery channel as a collapsible card (SCIO3's dialog idiom): brand
 * icon + name, then either a "Needs setup" badge or the one-line summary; the body
 * expands for setup and a Done click collapses it. PDF has no body (nothing to set
 * up). Only ADDED channels render — the rest wait in the "Add more" strip.
 */
function ChannelCard({
  type,
  open,
  needsSetup,
  summary,
  onToggleOpen,
  onRemove,
  children,
}: {
  type: ChannelKey;
  open: boolean;
  needsSetup: boolean;
  summary: string;
  onToggleOpen: () => void;
  onRemove: () => void;
  children?: ReactNode;
}): JSX.Element {
  const hasBody = Boolean(children);
  return (
    <div
      data-testid={`broadcast-channel-card-${type}`}
      className={cn(
        "rounded-lg border border-border transition-colors",
        open && hasBody && "border-primary ring-1 ring-primary",
      )}
    >
      <div className="flex items-center gap-2.5 px-3 py-2.5">
        <button
          type="button"
          onClick={hasBody ? onToggleOpen : undefined}
          className={cn(
            "flex min-w-0 flex-1 items-center gap-2.5 text-left",
            hasBody && "cursor-pointer",
          )}
        >
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-border bg-background">
            <ChannelIcon type={type} className="h-4 w-4" />
          </span>
          <span className="shrink-0 text-sm font-medium">{CHANNEL_LABELS[type]}</span>
          {needsSetup ? (
            <span className="shrink-0 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary">
              Needs setup
            </span>
          ) : (
            !open && (
              <span className="truncate text-xs text-muted-foreground">{summary}</span>
            )
          )}
        </button>
        <div className="flex shrink-0 items-center gap-0.5">
          {hasBody && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-muted-foreground"
              onClick={onToggleOpen}
              aria-label={open ? `Collapse ${CHANNEL_LABELS[type]}` : `Expand ${CHANNEL_LABELS[type]}`}
            >
              <ChevronDown className={cn("h-4 w-4 transition-transform", open && "rotate-180")} />
            </Button>
          )}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-muted-foreground hover:text-foreground"
            onClick={onRemove}
            aria-label={`Remove ${CHANNEL_LABELS[type]}`}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
      {hasBody && open && (
        <div className="space-y-2 border-t border-border/60 px-3 pb-3 pt-2.5">
          {children}
          <div className="flex justify-end">
            <Button type="button" variant="outline" size="sm" onClick={onToggleOpen}>
              Done
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export function BroadcastFormDialog({
  open,
  onOpenChange,
  mode = "project",
  projectId,
  projects = [],
  broadcast,
  onSubmit,
}: BroadcastFormDialogProps): JSX.Element {
  const isEdit = Boolean(broadcast);
  const isWorkspace = mode === "workspace";
  const navigate = useNavigate();

  const [title, setTitle] = useState("");
  // Capture is project-only (SCIO3 parity). `url` survives only for legacy
  // broadcasts created before the radio was removed — shown read-only on edit.
  const [sourceType, setSourceType] = useState<"optidev-project" | "url">(
    "optidev-project",
  );
  const [sourceUrl, setSourceUrl] = useState("");
  // Workspace mode: which published projects to capture + how to deliver the N images.
  const [selectedProjectIds, setSelectedProjectIds] = useState<string[]>([]);
  const [projectSearch, setProjectSearch] = useState("");
  const [deliveryMode, setDeliveryMode] = useState<DeliveryMode>("separate");

  const [preset, setPreset] = useState<SchedulePreset>("daily-9");
  const [customCron, setCustomCron] = useState("0 9 * * *");
  const [weeklyDay, setWeeklyDay] = useState("1");
  const [monthlyMode, setMonthlyMode] = useState<MonthlyMode>(DEFAULT_MONTHLY.mode);
  const [monthDay, setMonthDay] = useState(String(DEFAULT_MONTHLY.day));
  const [monthWeek, setMonthWeek] = useState(String(DEFAULT_MONTHLY.nth));
  const [monthWeekDay, setMonthWeekDay] = useState(String(DEFAULT_MONTHLY.weekday));
  const [timezone, setTimezone] = useState(BROWSER_TZ);

  const [slackEnabled, setSlackEnabled] = useState(true);
  const [slackConnectionId, setSlackConnectionId] = useState("");
  // The single source of truth for chosen Slack channels (listed picks + manually-added IDs).
  const [slackChannelIds, setSlackChannelIds] = useState<string[]>([]);
  const [slackMessage, setSlackMessage] = useState("");
  const [channelIdInput, setChannelIdInput] = useState(""); // "add by ID" field (private/unlisted)
  const [channelSearch, setChannelSearch] = useState("");
  const [webhookEnabled, setWebhookEnabled] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState("");
  const [teamsEnabled, setTeamsEnabled] = useState(false);
  const [teamsWebhookUrl, setTeamsWebhookUrl] = useState("");
  const [teamsMessage, setTeamsMessage] = useState("");
  const [emailEnabled, setEmailEnabled] = useState(false);
  const [emailRecipients, setEmailRecipients] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [pdfEnabled, setPdfEnabled] = useState(false);
  // Which added channel is expanded for setup — one at a time, SCIO3-style.
  const [openChannel, setOpenChannel] = useState<ChannelKey | null>("slack");

  const [submitAction, setSubmitAction] = useState<"create" | "test" | null>(
    null,
  );
  const submitting = submitAction !== null;

  const { connections: slackConnections, loading: slackLoading } =
    useSlackConnections(open && slackEnabled);
  const {
    channels: slackChannels,
    loading: channelsLoading,
    unavailable: channelsUnavailable,
    partial: channelsPartial,
  } = useSlackChannels(
    open && slackEnabled && slackConnectionId ? slackConnectionId : null,
  );
  // ONE source of truth — the chosen channels (from the list AND any manually-added IDs).
  const effectiveChannelIds = slackChannelIds;
  const filteredChannels = filterSlackChannels(slackChannels, channelSearch);
  const channelLabel = (id: string): string => {
    const found = slackChannels.find((c) => c.id === id);
    return found ? `#${found.name}` : id;
  };
  const removeSlackChannel = (id: string): void =>
    setSlackChannelIds((prev) => prev.filter((c) => c !== id));
  const addChannelIds = (): void => {
    const ids = parseChannelIds(channelIdInput);
    if (ids.length) {
      setSlackChannelIds((prev) => Array.from(new Set([...prev, ...ids])));
    }
    setChannelIdInput("");
  };

  const toggleChannel = (id: string): void => {
    setSlackChannelIds((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id],
    );
  };

  const toggleProject = (id: string): void => {
    // Only published projects are selectable (unpublished have no URL to capture).
    if (!projects.find((p) => p.id === id)?.published) return;
    setSelectedProjectIds((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id],
    );
  };
  // Published first, then alphabetical — so capturable projects surface at the top.
  const filteredProjects = projects
    .filter((p) => p.name.toLowerCase().includes(projectSearch.trim().toLowerCase()))
    .sort((a, b) =>
      a.published === b.published
        ? a.name.localeCompare(b.name)
        : a.published
          ? -1
          : 1,
    );

  // Hydrate form when opening (edit) or reset (create).
  useEffect(() => {
    if (!open) return;
    if (broadcast) {
      setTitle(broadcast.title);
      setSourceType(broadcast.source.type);
      setSourceUrl(broadcast.source.url ?? "");
      // Workspace broadcast: hydrate the project multi-select + delivery mode from sources[].
      const broadcastSources = broadcast.sources?.length
        ? broadcast.sources
        : [broadcast.source];
      setSelectedProjectIds(
        broadcastSources
          .map((s) => s.projectId)
          .filter((id): id is string => Boolean(id)),
      );
      setDeliveryMode(broadcast.deliveryMode ?? "separate");
      setProjectSearch("");
      setPreset(cronToPreset(broadcast.cron));
      setCustomCron(broadcast.cron);
      setWeeklyDay(weeklyDayOf(broadcast.cron) ?? "1");
      // Reopen a monthly broadcast in the mode that wrote it.
      const monthly = parseMonthlyCron(broadcast.cron) ?? DEFAULT_MONTHLY;
      setMonthlyMode(monthly.mode);
      setMonthDay(String(monthly.day));
      setMonthWeek(String(monthly.nth));
      setMonthWeekDay(String(monthly.weekday));
      setTimezone(broadcast.timezone || BROWSER_TZ);
      const slackChs = broadcast.channels.filter((c) => c.type === "slack");
      const webhook = broadcast.channels.find((c) => c.type === "webhook");
      const channelIds = slackChs
        .map((c) => c.config?.channelId as string)
        .filter(Boolean);
      setSlackEnabled(slackChs.length > 0);
      // slackConnectionId round-trips in config, so edit pre-selects the connection.
      setSlackConnectionId((slackChs[0]?.config?.slackConnectionId as string) ?? "");
      setSlackChannelIds(channelIds);
      setChannelIdInput("");
      setSlackMessage((slackChs[0]?.config?.message as string) ?? "");
      setChannelSearch("");
      setWebhookEnabled(Boolean(webhook));
      setWebhookUrl((webhook?.config?.url as string) ?? "");
      const teams = broadcast.channels.find((c) => c.type === "teams");
      setTeamsEnabled(Boolean(teams));
      setTeamsWebhookUrl((teams?.config?.webhookUrl as string) ?? "");
      setTeamsMessage((teams?.config?.message as string) ?? "");
      const email = broadcast.channels.find((c) => c.type === "email");
      setEmailEnabled(Boolean(email));
      setEmailRecipients(
        ((email?.config?.recipients as string[]) ?? []).join(", "),
      );
      setEmailSubject((email?.config?.subject as string) ?? "");
      setPdfEnabled(broadcast.channels.some((c) => c.type === "pdf"));
      // Edit opens with everything collapsed — the summaries tell the story.
      setOpenChannel(null);
    } else {
      setTitle("");
      setSourceType("optidev-project");
      setSourceUrl("");
      setSelectedProjectIds([]);
      setProjectSearch("");
      setDeliveryMode("separate");
      setPreset("daily-9");
      setCustomCron("0 9 * * *");
      setWeeklyDay("1");
      setMonthlyMode(DEFAULT_MONTHLY.mode);
      setMonthDay(String(DEFAULT_MONTHLY.day));
      setMonthWeek(String(DEFAULT_MONTHLY.nth));
      setMonthWeekDay(String(DEFAULT_MONTHLY.weekday));
      setTimezone(BROWSER_TZ);
      setSlackEnabled(true);
      setSlackConnectionId("");
      setSlackChannelIds([]);
      setChannelIdInput("");
      setChannelSearch("");
      setSlackMessage("");
      setWebhookEnabled(false);
      setWebhookUrl("");
      setTeamsEnabled(false);
      setTeamsWebhookUrl("");
      setTeamsMessage("");
      setEmailEnabled(false);
      setEmailRecipients("");
      setEmailSubject("");
      setPdfEnabled(false);
      // Slack starts added and open — it's the channel most broadcasts want first.
      setOpenChannel("slack");
    }
  }, [open, broadcast]);

  const cron =
    preset === "custom"
      ? customCron.trim()
      : preset === "weekly"
        ? weeklyCron(weeklyDay)
        : preset === "monthly"
          ? monthlyCron({
              mode: monthlyMode,
              day: clampMonthDay(monthDay),
              nth: Number(monthWeek),
              weekday: Number(monthWeekDay),
            })
          : PRESET_CRON[preset];

  const validationError = useMemo<string | null>(() => {
    if (!title.trim()) return "Give the broadcast a name.";
    if (isWorkspace && selectedProjectIds.length === 0)
      return "Pick at least one project to capture.";
    if (!isWorkspace && sourceType === "url" && !sourceUrl.trim())
      return "Enter the URL to capture.";
    if (
      !slackEnabled &&
      !webhookEnabled &&
      !teamsEnabled &&
      !emailEnabled &&
      !pdfEnabled
    )
      return "Add at least one channel.";
    if (slackEnabled && !slackConnectionId)
      return "Choose a Slack connection.";
    if (slackEnabled && effectiveChannelIds.length === 0)
      return "Pick at least one Slack channel.";
    if (webhookEnabled && !webhookUrl.trim())
      return "Enter the webhook URL.";
    if (teamsEnabled && !teamsWebhookUrl.trim())
      return "Enter the Teams webhook URL.";
    if (emailEnabled) {
      const parsed = parseRecipients(emailRecipients);
      if (parsed.length === 0) return "Enter at least one email recipient.";
      const bad = parsed.find((e) => !EMAIL_REGEX.test(e));
      if (bad) return `"${bad}" is not a valid email address.`;
    }
    if (!CRON_5_FIELD_REGEX.test(cron))
      return "Schedule must be a 5-field cron expression.";
    return null;
  }, [
    title,
    isWorkspace,
    selectedProjectIds,
    sourceType,
    sourceUrl,
    slackEnabled,
    slackConnectionId,
    effectiveChannelIds,
    webhookEnabled,
    webhookUrl,
    teamsEnabled,
    teamsWebhookUrl,
    emailEnabled,
    emailRecipients,
    pdfEnabled,
    cron,
  ]);

  const buildChannels = (): CreateBroadcastChannel[] => {
    const channels: CreateBroadcastChannel[] = [];
    if (slackEnabled) {
      // One engine channel per selected Slack channel (same connection).
      for (const channelId of effectiveChannelIds) {
        const channelName = slackChannels.find(
          (c) => c.id === channelId,
        )?.name;
        channels.push({
          type: "slack",
          slackConnectionId,
          channelId,
          channelName,
          message: slackMessage.trim() || undefined,
        });
      }
    }
    if (webhookEnabled) {
      channels.push({ type: "webhook", url: webhookUrl.trim() });
    }
    if (teamsEnabled) {
      channels.push({
        type: "teams",
        url: teamsWebhookUrl.trim(),
        message: teamsMessage.trim() || undefined,
      });
    }
    if (emailEnabled) {
      channels.push({
        type: "email",
        recipients: parseRecipients(emailRecipients),
        subject: emailSubject.trim() || undefined,
      });
    }
    if (pdfEnabled) {
      channels.push({ type: "pdf" });
    }
    return channels;
  };

  const handleSubmit = async (action: "create" | "test"): Promise<void> => {
    if (validationError || submitting) return;
    setSubmitAction(action);
    try {
      const base = {
        title: title.trim(),
        cron,
        timezone,
        channels: buildChannels(),
      };
      // Project mode only knows about ONE source, so saving a multi-source broadcast from here
      // would drop the others — the exact regression that got the per-project tab removed.
      // The row that opened this is supposed to be read-only; refuse rather than lose data.
      if (!isWorkspace && broadcast && (broadcast.sources?.length ?? 0) > 1) {
        throw new Error(
          "This broadcast captures several projects — edit it in workspace Broadcast.",
        );
      }
      const dto: CreateBroadcastDto = isWorkspace
        ? {
            ...base,
            sources: selectedProjectIds.map((id) => ({
              type: "optidev-project" as const,
              projectId: id,
            })),
            deliveryMode,
          }
        : {
            ...base,
            source:
              sourceType === "url"
                ? { type: "url", url: sourceUrl.trim() }
                : { type: "optidev-project", projectId: projectId as string },
          };
      await onSubmit(dto, { test: action === "test" });
      onOpenChange(false);
    } catch {
      // errors are surfaced via toast by the caller; keep the dialog open
    } finally {
      setSubmitAction(null);
    }
  };

  const goToConnectors = (): void => {
    onOpenChange(false);
    navigate("/dashboard/connectors");
  };

  // ── added-channel model (SCIO3 dialog idiom) ────────────────────────────────
  const channelFlags: Record<ChannelKey, boolean> = {
    slack: slackEnabled,
    teams: teamsEnabled,
    email: emailEnabled,
    pdf: pdfEnabled,
    webhook: webhookEnabled,
  };
  const setChannelFlag: Record<ChannelKey, (on: boolean) => void> = {
    slack: setSlackEnabled,
    teams: setTeamsEnabled,
    email: setEmailEnabled,
    pdf: setPdfEnabled,
    webhook: setWebhookEnabled,
  };
  const addedChannels = CHANNEL_ORDER.filter((t) => channelFlags[t]);
  const availableChannels = CHANNEL_ORDER.filter((t) => !channelFlags[t]);

  const addChannel = (type: ChannelKey): void => {
    setChannelFlag[type](true);
    setOpenChannel(type === "pdf" ? null : type); // PDF has nothing to set up
  };
  const removeChannel = (type: ChannelKey): void => {
    setChannelFlag[type](false);
    setOpenChannel((prev) => (prev === type ? null : prev));
  };
  const toggleOpen = (type: ChannelKey): void =>
    setOpenChannel((prev) => (prev === type ? null : type));

  const parsedRecipients = parseRecipients(emailRecipients);
  const channelNeedsSetup: Record<ChannelKey, boolean> = {
    slack: !slackConnectionId || effectiveChannelIds.length === 0,
    teams: !teamsWebhookUrl.trim(),
    email:
      parsedRecipients.length === 0 ||
      parsedRecipients.some((e) => !EMAIL_REGEX.test(e)),
    pdf: false,
    webhook: !webhookUrl.trim(),
  };
  const slackConnectionName = slackConnections.find(
    (c) => c.id === slackConnectionId,
  )?.name;
  const channelSummary: Record<ChannelKey, string> = {
    slack: (() => {
      const chans = effectiveChannelIds.map(channelLabel);
      const head = chans[0] ?? "";
      const more = chans.length > 1 ? ` +${chans.length - 1}` : "";
      return [head + more, slackConnectionName].filter(Boolean).join(" · ");
    })(),
    teams: "Workflows webhook",
    email: (() => {
      const head = parsedRecipients[0] ?? "";
      const more = parsedRecipients.length > 1 ? ` +${parsedRecipients.length - 1}` : "";
      const subject = emailSubject.trim() ? ` · “${emailSubject.trim()}”` : "";
      return head + more + subject;
    })(),
    pdf: "Saved to Snapshots",
    webhook: hostOf(webhookUrl.trim()),
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Column layout: header and footer are fixed, only the body scrolls. A sticky footer
          inside the scrolling grid used to float above the last channel card on tall forms. */}
      <DialogContent
        className="flex max-h-[100dvh] flex-col gap-0 overflow-hidden p-0 sm:max-h-[90vh] sm:max-w-2xl"
        data-testid="broadcast-form-dialog"
      >
        <DialogHeader className="px-6 pb-2 pt-6">
          <DialogTitle>{isEdit ? "Edit broadcast" : "New broadcast"}</DialogTitle>
          <DialogDescription>
            {isWorkspace
              ? "Capture several projects on one schedule and deliver them where your team works."
              : "Capture this app on a schedule and deliver it where your team works."}
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-6">
          <TooltipProvider>
            <div className="space-y-6 py-2">
              {/* Why the engine paused it (os-reporting-engine#4) — the mail's "Update broadcast
                  settings" link lands here, so say it before the form. Resume stays on the
                  list's switch. */}
              {isEdit && broadcast && isAutoPaused(broadcast) && (
                <Alert data-testid="broadcast-auto-paused-banner">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>
                    {broadcast.autoPausedAt
                      ? `Auto-paused on ${formatPausedAt(broadcast.autoPausedAt)} after repeated failed runs.`
                      : "Paused automatically after repeated failed runs."}
                  </AlertTitle>
                  <AlertDescription className="text-xs">
                    {broadcast.lastError && (
                      <p className="break-words">Last error: {broadcast.lastError}</p>
                    )}
                    <p>Fix the cause below, then switch the broadcast back on from the list.</p>
                  </AlertDescription>
                </Alert>
              )}
              {/* Name */}
              <div className="space-y-2">
                <Label htmlFor="broadcast-title">Broadcast name</Label>
                <Input
                  id="broadcast-title"
                  data-testid="broadcast-name-input"
                  placeholder="Weekly KPIs"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
                {/* Capture is project-only (SCIO3 parity) — a context line, not a control. */}
                {!isWorkspace &&
                  (sourceType === "url" ? (
                    <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <Globe className="h-3 w-3 shrink-0" />
                      <span className="truncate">Captures {sourceUrl}</span>
                      <span className="shrink-0 opacity-70">(legacy URL source)</span>
                    </p>
                  ) : (
                    <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <LayoutDashboard className="h-3 w-3 shrink-0" />
                      Captures this project’s published app
                    </p>
                  ))}
              </div>

              {/* Projects to capture — workspace mode */}
              {isWorkspace && (
                <div className="space-y-2">
                  <Label>Projects to capture</Label>
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      className="pl-8"
                      placeholder="Search projects…"
                      value={projectSearch}
                      onChange={(e) => setProjectSearch(e.target.value)}
                    />
                  </div>
                  <div className="overflow-hidden rounded-lg border border-border">
                    <ScrollArea className="h-48">
                      <div className="p-1">
                        {projects.length === 0 ? (
                          <p className="px-2 py-6 text-center text-sm text-muted-foreground">
                            No published projects yet. Publish a project to capture it.
                          </p>
                        ) : filteredProjects.length === 0 ? (
                          <p className="px-2 py-6 text-center text-sm text-muted-foreground">
                            No project found.
                          </p>
                        ) : (
                          filteredProjects.map((p) => (
                            <label
                              key={p.id}
                              className={cn(
                                "flex items-center gap-2 rounded px-2 py-1.5 text-sm",
                                p.published
                                  ? "cursor-pointer hover:bg-accent"
                                  : "cursor-not-allowed opacity-60",
                              )}
                            >
                              <Checkbox
                                checked={selectedProjectIds.includes(p.id)}
                                onCheckedChange={() => toggleProject(p.id)}
                                disabled={!p.published}
                              />
                              <LayoutDashboard className="h-4 w-4 shrink-0 text-muted-foreground" />
                              <span className="truncate">{p.name}</span>
                              {!p.published && (
                                <span className="ml-auto shrink-0 text-xs text-muted-foreground">
                                  Publish first
                                </span>
                              )}
                            </label>
                          ))
                        )}
                      </div>
                    </ScrollArea>
                    <div className="flex items-center justify-between border-t border-border bg-muted/40 px-3 py-1.5 text-xs text-muted-foreground">
                      <span>
                        <span className="font-medium text-foreground">
                          {selectedProjectIds.length} selected
                        </span>
                        {selectedProjectIds.length > 1 && " · captures run in this order"}
                      </span>
                      <button
                        type="button"
                        className="font-medium text-primary hover:underline disabled:opacity-50"
                        onClick={() => setSelectedProjectIds([])}
                        disabled={selectedProjectIds.length === 0}
                      >
                        Clear all
                      </button>
                    </div>
                  </div>

                  {/* How to deliver the N captures */}
                  {selectedProjectIds.length > 1 && (
                    <div className="space-y-1.5 pt-1">
                      <Label className="text-xs text-muted-foreground">
                        How to deliver
                      </Label>
                      <RadioGroup
                        value={deliveryMode}
                        onValueChange={(v) => setDeliveryMode(v as DeliveryMode)}
                        className="grid grid-cols-1 gap-2 sm:grid-cols-3"
                      >
                        {DELIVERY_MODES.map((m) => (
                          <label
                            key={m.value}
                            className={cn(
                              "flex cursor-pointer items-start gap-2 rounded-lg border border-border px-3 py-2",
                              deliveryMode === m.value &&
                                "border-primary ring-1 ring-primary",
                            )}
                          >
                            <RadioGroupItem value={m.value} className="mt-0.5" />
                            <span className="min-w-0">
                              <span className="block text-xs font-semibold text-foreground">
                                {m.title}
                              </span>
                              <span className="block text-[11px] leading-snug text-muted-foreground">
                                {m.desc}
                              </span>
                            </span>
                          </label>
                        ))}
                      </RadioGroup>
                    </div>
                  )}
                </div>
              )}

              {/* Send to — added channels as cards + an "Add more" strip (SCIO3 idiom) */}
              <div className="space-y-2">
                <Label>Send to</Label>
                {addedChannels.length === 0 && (
                  <p className="rounded-lg border border-dashed border-border px-3 py-4 text-center text-sm text-muted-foreground">
                    No channel yet — add one below.
                  </p>
                )}
                {addedChannels.map((type) => {
                  // PDF gets NO body (nothing to set up) → header-only card.
                  const body =
                    type === "slack" ? (
                      <div className="space-y-2">
                        {slackLoading ? (
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Loading Slack connections…
                          </div>
                        ) : slackConnections.length === 0 ? (
                          <button
                            type="button"
                            onClick={goToConnectors}
                            className="flex w-full items-center justify-between rounded-md border border-dashed border-border px-3 py-2 text-left text-sm text-muted-foreground transition hover:border-primary hover:text-foreground"
                          >
                            No Slack connection yet — connect Slack to deliver here.
                            <span className="ml-2 flex items-center gap-1 font-medium text-primary">
                              Connect Slack <ArrowRight className="h-3.5 w-3.5" />
                            </span>
                          </button>
                        ) : (
                          <div className="space-y-1.5">
                            <Label className="text-xs text-muted-foreground">
                              Workspace
                            </Label>
                            <Select
                              value={slackConnectionId}
                              onValueChange={(v) => {
                                setSlackConnectionId(v);
                                setSlackChannelIds([]);
                                setChannelIdInput("");
                                setChannelSearch("");
                              }}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Choose a Slack connection" />
                              </SelectTrigger>
                              <SelectContent>
                                {slackConnections.map((c) => (
                                  <SelectItem key={c.id} value={c.id}>
                                    {c.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        )}

                        {/* Channels: tick from the list and/or add IDs (private/unlisted). Every
                            chosen channel shows as a removable chip — one clear source of truth. */}
                        {slackConnectionId && (
                          <div className="space-y-2">
                            {channelsLoading ? (
                              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <Loader2 className="h-4 w-4 animate-spin" />
                                Loading channels…
                              </div>
                            ) : (
                              <>
                                {slackChannels.length > 0 && (
                                  <>
                                    {/* Inline (no portal) so items stay clickable in the modal. */}
                                    <div className="relative">
                                      <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                                      <Input
                                        className="pl-8"
                                        placeholder="Search channels…"
                                        value={channelSearch}
                                        onChange={(e) => setChannelSearch(e.target.value)}
                                      />
                                    </div>
                                    <ScrollArea className="h-40 rounded-md border border-border">
                                      <div className="p-1">
                                        {filteredChannels.length === 0 ? (
                                          <p className="px-2 py-6 text-center text-sm text-muted-foreground">
                                            No channel found.
                                          </p>
                                        ) : (
                                          filteredChannels.map((ch) => (
                                            <label
                                              key={ch.id}
                                              className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-accent"
                                            >
                                              <Checkbox
                                                checked={slackChannelIds.includes(ch.id)}
                                                onCheckedChange={() => toggleChannel(ch.id)}
                                              />
                                              <span className="truncate">#{ch.name}</span>
                                            </label>
                                          ))
                                        )}
                                      </div>
                                    </ScrollArea>
                                  </>
                                )}

                                {/* Add by ID — for private channels, or when listing is unavailable. */}
                                <div className="flex gap-2">
                                  <Input
                                    placeholder="Add channel ID (e.g. C0123ABC)"
                                    value={channelIdInput}
                                    onChange={(e) => setChannelIdInput(e.target.value)}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter") {
                                        e.preventDefault();
                                        addChannelIds();
                                      }
                                    }}
                                  />
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={addChannelIds}
                                  >
                                    Add
                                  </Button>
                                </div>
                                {channelsUnavailable && slackChannels.length === 0 && (
                                  <p className="text-xs text-muted-foreground">
                                    Couldn't list channels (the Slack app may need the{" "}
                                    <code>channels:read</code> scope) — add channel IDs above.
                                  </p>
                                )}
                                {channelsPartial && (
                                  <p className="text-xs text-muted-foreground">
                                    Not all channels could be loaded. If yours is missing, add it by
                                    channel ID.
                                  </p>
                                )}

                                {/* Selected — always visible + removable (incl. unlisted IDs). */}
                                {slackChannelIds.length > 0 ? (
                                  <div className="flex flex-wrap gap-1.5">
                                    {slackChannelIds.map((id) => (
                                      <span
                                        key={id}
                                        className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/50 px-2 py-0.5 text-xs"
                                      >
                                        <span className="max-w-[160px] truncate">
                                          {channelLabel(id)}
                                        </span>
                                        <button
                                          type="button"
                                          onClick={() => removeSlackChannel(id)}
                                          aria-label={`Remove ${channelLabel(id)}`}
                                          className="text-muted-foreground hover:text-foreground"
                                        >
                                          <X className="h-3 w-3" />
                                        </button>
                                      </span>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="text-xs text-muted-foreground">
                                    No channels selected yet.
                                  </p>
                                )}
                              </>
                            )}
                          </div>
                        )}

                        <Textarea
                          placeholder="Optional message shown with the broadcast"
                          rows={2}
                          value={slackMessage}
                          onChange={(e) => setSlackMessage(e.target.value)}
                        />
                      </div>
                    ) : type === "webhook" ? (
                      <Input
                        data-testid="broadcast-webhook-url"
                        placeholder="https://hooks.example.com/broadcast"
                        value={webhookUrl}
                        onChange={(e) => setWebhookUrl(e.target.value)}
                      />
                    ) : type === "teams" ? (
                      <div className="space-y-2">
                        <Input
                          placeholder="https://prod-00.westus.logic.azure.com:443/workflows/…"
                          value={teamsWebhookUrl}
                          onChange={(e) => setTeamsWebhookUrl(e.target.value)}
                        />
                        <p className="text-xs text-muted-foreground">
                          In Teams: Workflows → “Post to a channel when a webhook
                          request is received” → copy the URL and paste it here.{" "}
                          <a
                            href={TEAMS_WEBHOOK_HELP_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-primary hover:underline"
                          >
                            How to create a Teams webhook
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        </p>
                        <Textarea
                          placeholder="Optional message shown with the broadcast"
                          rows={2}
                          value={teamsMessage}
                          onChange={(e) => setTeamsMessage(e.target.value)}
                        />
                      </div>
                    ) : type === "email" ? (
                      // Email — delivered via the OptiDev Customer.io workspace (per-product
                      // template; engine EmailAdapter routes by tenantRef.product).
                      <div className="space-y-2">
                        <Textarea
                          placeholder="Recipients, comma or newline separated (e.g. team@company.com, ops@company.com)"
                          rows={2}
                          value={emailRecipients}
                          onChange={(e) => setEmailRecipients(e.target.value)}
                        />
                        <Input
                          placeholder="Subject (optional)"
                          value={emailSubject}
                          onChange={(e) => setEmailSubject(e.target.value)}
                        />
                      </div>
                    ) : undefined; // PDF: header-only card, nothing to set up

                  return (
                    <ChannelCard
                      key={type}
                      type={type}
                      open={openChannel === type}
                      needsSetup={channelNeedsSetup[type]}
                      summary={channelSummary[type]}
                      onToggleOpen={() => toggleOpen(type)}
                      onRemove={() => removeChannel(type)}
                    >
                      {body}
                    </ChannelCard>
                  );
                })}

                {availableChannels.length > 0 && (
                  <div className="flex items-center gap-2 pt-1">
                    <span className="text-xs text-muted-foreground">Add more:</span>
                    {availableChannels.map((type) => (
                      <Tooltip key={type} delayDuration={200}>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            data-testid={`broadcast-${type}-toggle`}
                            onClick={() => addChannel(type)}
                            aria-label={`Add ${CHANNEL_LABELS[type]}`}
                            className="flex h-8 w-8 items-center justify-center rounded-lg border border-border bg-background transition-colors hover:border-primary"
                          >
                            <ChannelIcon type={type} className="h-4 w-4" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="top">
                          {type === "pdf"
                            ? "PDF — save to Snapshots"
                            : CHANNEL_LABELS[type]}
                        </TooltipContent>
                      </Tooltip>
                    ))}
                  </div>
                )}
              </div>

              {/* When */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <CalendarClock className="h-4 w-4" />
                  When to send
                </Label>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <Select
                    value={preset}
                    onValueChange={(v) => setPreset(v as SchedulePreset)}
                  >
                    <SelectTrigger className="sm:flex-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SCHEDULE_PRESETS.map((p) => (
                        <SelectItem key={p.value} value={p.value}>
                          {p.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {preset === "weekly" && (
                    <Select value={weeklyDay} onValueChange={setWeeklyDay}>
                      <SelectTrigger className="sm:flex-1" aria-label="Day of week">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {WEEKDAYS.map((d) => (
                          <SelectItem key={d.value} value={d.value}>
                            Every {d.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  <Select value={timezone} onValueChange={setTimezone}>
                    <SelectTrigger className="sm:w-52" aria-label="Timezone">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {timezoneOptions(timezone).map((tz) => (
                        <SelectItem key={tz} value={tz}>
                          {tz}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {preset === "monthly" && (
                  <RadioGroup
                    value={monthlyMode}
                    onValueChange={(v) => setMonthlyMode(v as MonthlyMode)}
                    className="gap-2.5 rounded-lg border border-border p-3"
                    data-testid="broadcast-monthly"
                  >
                    <div className="flex items-center gap-3">
                      <RadioGroupItem value="dayOfMonth" id="broadcast-monthly-day" />
                      <Label
                        htmlFor="broadcast-monthly-day"
                        className="flex items-center gap-2 font-normal"
                      >
                        On day
                        <Input
                          type="number"
                          inputMode="numeric"
                          min={1}
                          max={31}
                          step={1}
                          value={monthDay}
                          onChange={(e) => setMonthDay(e.target.value)}
                          onBlur={() => setMonthDay(String(clampMonthDay(monthDay)))}
                          className="h-8 w-16"
                          disabled={monthlyMode !== "dayOfMonth"}
                          aria-label="Day of the month"
                          data-testid="broadcast-month-day"
                        />
                      </Label>
                    </div>
                    {/* Cron skips a month that has no such day — say so rather than let the
                        broadcast go quiet in February. */}
                    {monthlyMode === "dayOfMonth" &&
                      monthDaySkipsMonths(clampMonthDay(monthDay)) && (
                        <p
                          className="pl-7 text-xs text-muted-foreground"
                          data-testid="broadcast-month-day-note"
                        >
                          Months without this day are skipped. Use Last day of the month to run
                          every month.
                        </p>
                      )}
                    <div className="flex items-center gap-3">
                      <RadioGroupItem value="lastDay" id="broadcast-monthly-last-day" />
                      <Label htmlFor="broadcast-monthly-last-day" className="font-normal">
                        Last day of the month
                      </Label>
                    </div>
                    <div className="flex flex-wrap items-center gap-3">
                      <RadioGroupItem value="dayOfWeek" id="broadcast-monthly-weekday" />
                      <Label
                        htmlFor="broadcast-monthly-weekday"
                        className="flex flex-wrap items-center gap-2 font-normal"
                      >
                        On the
                        <Select
                          value={monthWeek}
                          onValueChange={setMonthWeek}
                          disabled={monthlyMode !== "dayOfWeek"}
                        >
                          <SelectTrigger
                            className="h-8 w-24"
                            aria-label="Week of the month"
                            data-testid="broadcast-month-week"
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {MONTH_WEEKS.map((w) => (
                              <SelectItem key={w.value} value={w.value}>
                                {w.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Select
                          value={monthWeekDay}
                          onValueChange={setMonthWeekDay}
                          disabled={monthlyMode !== "dayOfWeek"}
                        >
                          <SelectTrigger
                            className="h-8 w-32"
                            aria-label="Weekday"
                            data-testid="broadcast-month-weekday"
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {WEEKDAYS.map((d) => (
                              <SelectItem key={d.value} value={d.value}>
                                {d.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </Label>
                    </div>
                  </RadioGroup>
                )}
                {preset === "custom" && (
                  <Input
                    placeholder="0 9 * * 1"
                    value={customCron}
                    onChange={(e) => setCustomCron(e.target.value)}
                  />
                )}
              </div>
            </div>
          </TooltipProvider>
        </div>

        {/* Fixed footer: the body above scrolls (many projects / channels) and on a phone the
            Create button used to sit below the fold — the actions stay visible. */}
        <DialogFooter className="flex-col gap-2 border-t border-border bg-background px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
          <span className="text-xs text-muted-foreground">
            {validationError ??
              (isEdit
                ? "Save your changes."
                : "You can run it from the broadcast list right after creating it.")}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              data-testid="broadcast-cancel-btn"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              data-testid="broadcast-submit-btn"
              onClick={() => void handleSubmit("create")}
              disabled={Boolean(validationError) || submitting}
            >
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEdit ? "Save changes" : "Create broadcast"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
